import { DateTime } from 'luxon';
import { escapeRegExp } from 'lodash-es';

export const visitGroup = {
  today: 'today',
  yesterday: 'yesterday',
  pastWeek: 'pastWeek',
  past30Days: 'past30Days',
  earlier: 'earlier',
};

const visitGroupOrder = [visitGroup.today, visitGroup.yesterday, visitGroup.pastWeek, visitGroup.past30Days, visitGroup.earlier];

const visitGroupOf = (visitedAt, startOfToday) => {
  if (typeof visitedAt !== 'number') {
    return visitGroup.earlier;
  }
  const at = DateTime.fromMillis(visitedAt);
  if (at >= startOfToday) {
    return visitGroup.today;
  }
  if (at >= startOfToday.minus({ days: 1 })) {
    return visitGroup.yesterday;
  }
  if (at >= startOfToday.minus({ days: 7 })) {
    return visitGroup.pastWeek;
  }
  if (at >= startOfToday.minus({ days: 30 })) {
    return visitGroup.past30Days;
  }
  return visitGroup.earlier;
};

export const groupVisitsByRecency = (visits, now = DateTime.local()) => {
  const startOfToday = now.startOf('day');
  const buckets = new Map(visitGroupOrder.map((key) => [key, []]));
  visits.forEach((visit) => { buckets.get(visitGroupOf(visit.visitedAt, startOfToday)).push(visit); });

  return visitGroupOrder
    .map((key) => ({ key, items: buckets.get(key) }))
    .filter(({ items }) => items.length > 0);
};

const searchTerms = (search) => search.toLowerCase().split(' ').filter((term) => term !== '');

export const matchesSearch = (title, search) => {
  const normalizedTitle = (title ?? '').toLowerCase();
  return searchTerms(search).every((term) => normalizedTitle.includes(term));
};

export const highlightMatches = (title, search) => {
  const terms = searchTerms(search);
  if (terms.length === 0) {
    return [{ text: title, match: false }];
  }

  // longest first, so a term that prefixes another one cannot claim the shorter match
  const alternatives = [...terms].sort((a, b) => b.length - a.length).map(escapeRegExp);
  return title
    .split(new RegExp(`(${alternatives.join('|')})`, 'gi'))
    .map((text, index) => ({ text, match: index % 2 === 1 }))
    .filter(({ text }) => text !== '');
};

const matchScore = (title, search) => {
  const normalizedTitle = (title ?? '').toLowerCase();
  const [firstTerm = ''] = searchTerms(search);
  if (normalizedTitle.startsWith(search.trim().toLowerCase())) {
    return 2;
  }
  if (normalizedTitle.split(/\s+/).some((word) => word.startsWith(firstTerm))) {
    return 1;
  }
  return 0;
};

export const rankByMatch = (items, search, titleOf = ({ title }) => title) => items
  .map((item) => ({ item, score: matchScore(titleOf(item), search) }))
  .sort((a, b) => b.score - a.score)
  .map(({ item }) => item);
